// The outcome of reading ONE record by id (BL-1981): found, not found, or a
// failed read. `useRecordById` renders from it; every typed-client reader
// produces it through `recordReadFromEnvelope`, so "404 means not found" is
// decided in exactly one place.

import type { StatusEnvelope } from '@auctogon/core/api/registry';

/**
 * What a by-id read came back with.
 *
 * `not-found` is final (the record does not exist, or is not this tenant's —
 * both routes answer 404). `error` is retryable (a 5xx, a non-success envelope).
 */
export type RecordRead<T> =
  | { kind: 'found'; data: T }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string };

/**
 * Map a `.withStatus()` reply to a {@link RecordRead}.
 *
 * @param reply - The HTTP status and the parsed envelope.
 * @returns `not-found` on 404; `error` with the server's message on any other
 *   failure, or on a success that carries no record; otherwise `found`.
 */
export function recordReadFromEnvelope<T>(reply: StatusEnvelope<T | null | undefined>): RecordRead<T> {
  if (reply.status === 404) return { kind: 'not-found' };
  const { body } = reply;
  if (!body.success) return { kind: 'error', message: body.error };
  // A success with no record is a broken read, never a blank record.
  if (body.data == null) return { kind: 'error', message: 'The server returned no record.' };
  return { kind: 'found', data: body.data };
}

/**
 * The BUG-1257 contract the by-id reads that return `T | null` share (the two
 * auction reads, the customer page's read): a real 404 is `null`, any other
 * failure throws (with the server's message), so a caller can tell "deleted"
 * from "couldn't load".
 *
 * @param outcome - the read's outcome.
 * @returns The record, or `null` when it was not found.
 * @throws When the read failed.
 */
export function nullOnNotFound<T>(outcome: RecordRead<T>): T | null {
  if (outcome.kind === 'found') return outcome.data;
  if (outcome.kind === 'not-found') return null;
  throw new Error(outcome.message);
}
